import React, { useEffect, useState, useRef } from "react";
import { Link, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, ShoppingCart, Tag, Upload, Plus, Pencil, CreditCard } from "lucide-react";
import ImageUploader, { imgUrl } from "@/components/ImageUploader";

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8">Loading…</div>;
  if (!user || user.role !== "admin") return <div className="p-8 text-center"><div className="font-serif text-2xl text-[#0B1F3A]">Admin access only</div><Link to="/login" className="underline text-sm" data-testid="admin-login-link">Sign in as admin</Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="eyebrow mb-2">Admin</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[#0B1F3A] mb-6">Nisa Libas CMS</h1>
      <div className="flex flex-wrap gap-2 mb-8 border-b" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
        {[
          ["/admin", "Overview", <LayoutDashboard size={14} />],
          ["/admin/products", "Products", <Package size={14} />],
          ["/admin/orders", "Orders", <ShoppingCart size={14} />],
          ["/admin/coupons", "Coupons", <Tag size={14} />],
        ].map(([to, l, i]) => (
          <NavLink key={to} to={to} end={to === "/admin"} className={({ isActive }) => `px-4 py-3 text-xs tracking-widest uppercase flex items-center gap-2 ${isActive ? "text-[#D4AF37] border-b-2 border-[#D4AF37] -mb-px" : "text-[#1E293B]/60"}`} data-testid={`admin-tab-${l}`}>{i} {l}</NavLink>
        ))}
      </div>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="products" element={<Products />} />
        <Route path="orders" element={<Orders />} />
        <Route path="coupons" element={<Coupons />} />
      </Routes>
    </div>
  );
}

function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <div>Loading…</div>;
  const modeCls = a.payment_mode === "live" ? "bg-emerald-100 text-emerald-800" : a.payment_mode === "mock" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Revenue", inr(a.revenue)], ["Orders", a.orders], ["Paid", a.paid], ["Products", a.products]].map(([l, v]) => (
          <div key={l} className="bg-white p-5 gold-border">
            <div className="eyebrow mb-1">{l}</div>
            <div className="font-serif text-2xl text-[#0B1F3A]" data-testid={`stat-${l}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="bg-white p-6 gold-border">
          <div className="eyebrow mb-3 flex items-center gap-2"><CreditCard size={14} /> Payments</div>
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-xs px-2 py-1 tracking-widest uppercase ${modeCls}`} data-testid="payment-mode">{a.payment_mode}</span>
            <span className="text-sm text-[#1E293B]/70">Razorpay {a.payment_mode === "live" ? "live" : "not connected"}</span>
          </div>
          {a.payment_mode !== "live" && (
            <div className="text-xs text-[#1E293B]/60 leading-relaxed mt-2">
              Add <code className="bg-[#FCE4EC] px-1">RAZORPAY_KEY_ID</code> and <code className="bg-[#FCE4EC] px-1">RAZORPAY_KEY_SECRET</code> in backend .env, then restart. Test cards work with any key starting <code>rzp_test_…</code>
            </div>
          )}
          <div className="text-xs mt-3">
            <span className="text-[#1E293B]/60">Image storage: </span>
            <span className={a.storage_ready ? "text-emerald-700" : "text-red-700"} data-testid="storage-status">{a.storage_ready ? "ready" : "unavailable"}</span>
          </div>
        </div>
        <div className="bg-white p-6 gold-border">
          <div className="eyebrow mb-3">Top products</div>
          {a.top_products.length === 0 ? <div className="text-sm text-[#1E293B]/60">No sales yet.</div> :
            a.top_products.map((t) => <div key={t.name} className="flex justify-between py-2 border-b" style={{ borderColor: "rgba(212,175,55,0.15)" }}><span className="text-sm">{t.name}</span><span className="font-semibold text-[#D4AF37]">{t.qty} sold</span></div>)}
        </div>
      </div>
    </div>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/products?limit=200").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const csvImport = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/products/import-csv", fd); toast.success(`Imported ${data.created}`); load(); }
    catch { toast.error("Import failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setShowAdd(true)} className="bg-[#0B1F3A] text-[#FFF9E6] px-4 py-2 text-xs tracking-widest uppercase flex items-center gap-2" data-testid="add-product-btn"><Plus size={14} /> Add product</button>
        <button onClick={() => fileRef.current.click()} className="border border-[#0B1F3A] text-[#0B1F3A] px-4 py-2 text-xs tracking-widest uppercase flex items-center gap-2" data-testid="import-csv-btn"><Upload size={14} /> Import CSV</button>
        <input ref={fileRef} type="file" accept=".csv" onChange={csvImport} className="hidden" data-testid="csv-file" />
        <div className="text-xs text-[#1E293B]/60 ml-auto self-center">{items.length} products</div>
      </div>
      <div className="bg-white gold-border overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-[#FCE4EC]"><tr>{["", "Name", "Category", "Price", "Stock", "Actions"].map((h) => <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#0B1F3A]/70">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }} data-testid={`admin-product-${p.id}`}>
                <td className="p-2"><img src={imgUrl(p.images?.[0])} className="w-10 h-14 object-cover" alt="" /></td>
                <td className="p-2 font-serif">{p.name}</td>
                <td className="p-2 text-xs">{p.category} · {p.subcategory}</td>
                <td className="p-2 text-[#D4AF37] font-semibold">{inr(p.price)}{p.compare_at_price > p.price && <span className="ml-1 text-xs text-[#1E293B]/40 line-through font-normal">{inr(p.compare_at_price)}</span>}</td>
                <td className="p-2">{p.stock}</td>
                <td className="p-2">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} className="text-xs text-[#0B1F3A] hover:underline flex items-center gap-1" data-testid={`edit-prod-${p.id}`}><Pencil size={12} /> Edit</button>
                    <button onClick={async () => { if (!window.confirm(`Delete ${p.name}?`)) return; await api.delete(`/admin/products/${p.id}`); load(); toast.success("Deleted"); }} className="text-xs text-red-600 hover:underline" data-testid={`del-prod-${p.id}`}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <ProductForm mode="add" onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <ProductForm mode="edit" product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductForm({ mode, product, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [f, setF] = useState(() => isEdit ? {
    name: product.name, category: product.category, subcategory: product.subcategory,
    mrp: product.compare_at_price > product.price ? product.compare_at_price : product.price,
    sale: product.compare_at_price > product.price ? product.price : "",
    fabric: product.fabric || "",
    colors: (product.colors || []).join(","), sizes: (product.sizes || []).join(","),
    description: product.description || "", story: product.story || "",
    featured: !!product.featured, stock: product.stock ?? 50,
  } : {
    name: "", category: "women", subcategory: "Sarees", mrp: 1999, sale: "", fabric: "Cotton",
    colors: "Ivory", sizes: "S,M,L", description: "", story: "", featured: false, stock: 50,
  });
  const [images, setImages] = useState(product?.images || []);

  const submit = async (e) => {
    e.preventDefault();
    const mrp = Number(f.mrp), sale = f.sale === "" ? 0 : Number(f.sale);
    if (sale && sale > mrp) return toast.error("Sale price must be lower than actual price (MRP)");
    const onSale = sale > 0 && sale < mrp;
    const payload = {
      name: f.name, category: f.category, subcategory: f.subcategory,
      price: onSale ? sale : mrp, compare_at_price: onSale ? mrp : null,
      fabric: f.fabric, colors: f.colors.split(",").map((x) => x.trim()).filter(Boolean),
      sizes: f.sizes.split(",").map((x) => x.trim()).filter(Boolean),
      images,
      description: f.description, story: f.story, featured: f.featured, stock: Number(f.stock),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FCE4EC] max-w-3xl w-full p-6 max-h-[90vh] overflow-auto">
        <h3 className="font-serif text-xl text-[#0B1F3A] mb-4">{isEdit ? "Edit product" : "Add product"}</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {[["name","Name"],["mrp","Actual price (MRP) ₹"],["fabric","Fabric"],["colors","Colors (comma)"],["sizes","Sizes (comma)"],["stock","Stock"]].map(([k, l]) => (
            <label key={k}><div className="text-xs text-[#1E293B]/60 mb-1">{l}</div>
              <input value={f[k]} onChange={(e) => setF({...f, [k]: e.target.value})} required className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={`np-${k}`} />
            </label>
          ))}
          <label><div className="text-xs text-[#1E293B]/60 mb-1">Sale price ₹ (optional)</div>
            <input type="number" value={f.sale} onChange={(e) => setF({...f, sale: e.target.value})} placeholder="Leave empty to sell at MRP" className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-sale" />
          </label>
          <label><div className="text-xs text-[#1E293B]/60 mb-1">Category</div>
            <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-category"><option value="women">Women</option><option value="kids">Kids</option></select>
          </label>
          <label><div className="text-xs text-[#1E293B]/60 mb-1">Subcategory</div>
            <input value={f.subcategory} onChange={(e) => setF({...f, subcategory: e.target.value})} className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-sub" />
          </label>
          <div className="md:col-span-2">
            <div className="text-xs text-[#1E293B]/60 mb-2">Product images</div>
            <ImageUploader images={images} onChange={setImages} testid="np-uploader" />
          </div>
          <label className="md:col-span-2"><div className="text-xs text-[#1E293B]/60 mb-1">Description</div>
            <textarea value={f.description} onChange={(e) => setF({...f, description: e.target.value})} rows={2} className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-desc" />
          </label>
          <label className="md:col-span-2"><div className="text-xs text-[#1E293B]/60 mb-1">Story behind the stitch</div>
            <textarea value={f.story} onChange={(e) => setF({...f, story: e.target.value})} rows={2} className="w-full border px-2 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-story" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.featured} onChange={(e) => setF({...f, featured: e.target.checked})} data-testid="np-featured" /> Featured on home
          </label>
        </div>
        <div className="flex gap-2 mt-4 sticky bottom-0 bg-[#FCE4EC] py-2">
          <button className="bg-[#0B1F3A] text-[#FFF9E6] px-5 py-2 text-xs tracking-widest uppercase" data-testid="np-save">{isEdit ? "Save changes" : "Create"}</button>
          <button type="button" onClick={onClose} className="border px-5 py-2 text-xs tracking-widest uppercase" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="np-cancel">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateOrder = async (id, patch) => {
    await api.put(`/admin/orders/${id}`, patch); load(); toast.success("Updated");
  };

  return (
    <div className="bg-white gold-border overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-[#FCE4EC]"><tr>{["Order", "Customer", "Total", "Delivery", "Payment", "Shipping status", "Tracking", "Supplier"].map((h) => <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#0B1F3A]/70">{h}</th>)}</tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }} data-testid={`admin-order-${o.order_no}`}>
              <td className="p-2 font-serif">{o.order_no}</td>
              <td className="p-2 text-xs">{o.address.full_name}<br /><span className="text-[#1E293B]/60">{o.email}</span></td>
              <td className="p-2 text-[#D4AF37] font-semibold">{inr(o.total)}</td>
              <td className="p-2 text-xs" data-testid={`delivery-${o.order_no}`}>{o.delivery_option === "express" ? "Express (2 days)" : "Standard (5–7 days)"}{o.shipping > 0 && <div className="text-[#1E293B]/60">{inr(o.shipping)}</div>}</td>
              <td className="p-2"><PaymentCell o={o} onUpdate={updateOrder} /></td>
              <td className="p-2">
                <select value={o.status} onChange={(e) => updateOrder(o.id, { status: e.target.value })} className="text-xs border px-2 py-1" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={`status-${o.order_no}`}>
                  {["pending","processing","packed","shipped","delivered","cancelled"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="p-2">
                <input placeholder="Tracking #" defaultValue={o.tracking_no || ""} onBlur={(e) => e.target.value !== (o.tracking_no||"") && updateOrder(o.id, { tracking_no: e.target.value })} className="text-xs border px-2 py-1 w-28" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={`tn-${o.order_no}`} />
              </td>
              <td className="p-2">
                <input placeholder="Supplier" defaultValue={o.supplier || ""} onBlur={(e) => e.target.value !== (o.supplier||"") && updateOrder(o.id, { supplier: e.target.value })} className="text-xs border px-2 py-1 w-24" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={`sup-${o.order_no}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PAY_LABEL = { paid: "Paid", pending: "Pending", pending_verification: "Payment pending verification", rejected: "Rejected", failed: "Failed" };
const PAY_CLS = { paid: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", failed: "bg-red-100 text-red-800" };

function PaymentCell({ o, onUpdate }) {
  const isUpi = o.payment_method === "upi";
  return (
    <div className="text-xs space-y-1">
      <span className={`inline-block px-2 py-0.5 ${PAY_CLS[o.payment_status] || "bg-amber-100 text-amber-800"}`} data-testid={`pay-status-${o.order_no}`}>{PAY_LABEL[o.payment_status] || o.payment_status}</span>
      <div className="text-[#1E293B]/60">{isUpi ? "UPI" : "Online"}</div>
      {isUpi && <div className="font-mono" data-testid={`utr-${o.order_no}`}>UTR: {o.upi_utr}</div>}
      {isUpi && o.payment_status !== "paid" && (
        <div className="flex gap-1">
          <button onClick={() => onUpdate(o.id, { payment_status: "paid" })} className="px-2 py-0.5 bg-emerald-600 text-white" data-testid={`mark-paid-${o.order_no}`}>Mark paid</button>
          {o.payment_status !== "rejected" && <button onClick={() => onUpdate(o.id, { payment_status: "rejected" })} className="px-2 py-0.5 bg-red-600 text-white" data-testid={`mark-rejected-${o.order_no}`}>Reject</button>}
        </div>
      )}
    </div>
  );
}

function Coupons() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ code: "", kind: "percent", value: 10, min_order: 0 });
  const load = () => api.get("/admin/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/admin/coupons", f); toast.success("Created"); load(); setF({ code: "", kind: "percent", value: 10, min_order: 0 }); } catch { toast.error("Failed"); } };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={submit} className="bg-white p-5 gold-border space-y-3 h-fit">
        <div className="font-serif text-lg text-[#0B1F3A]">New coupon</div>
        <input placeholder="Code" value={f.code} onChange={(e) => setF({...f, code: e.target.value})} className="w-full border px-3 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="cp-code" />
        <select value={f.kind} onChange={(e) => setF({...f, kind: e.target.value})} className="w-full border px-3 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="cp-kind"><option value="percent">Percent</option><option value="flat">Flat ₹</option></select>
        <input type="number" placeholder="Value" value={f.value} onChange={(e) => setF({...f, value: e.target.value})} className="w-full border px-3 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="cp-value" />
        <input type="number" placeholder="Min order ₹" value={f.min_order} onChange={(e) => setF({...f, min_order: e.target.value})} className="w-full border px-3 py-2 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="cp-min" />
        <button className="w-full bg-[#0B1F3A] text-[#FFF9E6] py-2 text-xs tracking-widest uppercase" data-testid="cp-save">Create</button>
      </form>
      <div className="md:col-span-2 bg-white gold-border">
        <table className="w-full text-sm">
          <thead className="bg-[#FCE4EC]"><tr>{["Code","Type","Value","Min order","Active"].map((h) => <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#0B1F3A]/70">{h}</th>)}</tr></thead>
          <tbody>{list.map((c) => (
            <tr key={c.id} className="border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }}><td className="p-2 font-serif">{c.code}</td><td className="p-2">{c.kind}</td><td className="p-2">{c.kind === "percent" ? `${c.value}%` : inr(c.value)}</td><td className="p-2">{inr(c.min_order)}</td><td className="p-2">{c.active ? "Yes" : "No"}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
